import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCallback, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { User } from "../types/user";
import { useLanguage } from "../contexts/LanguageContext";

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { t } = useLanguage();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'users'), orderBy('displayName', 'asc'))
      const querySnapshot = await getDocs(q);
      const loadedUsers = querySnapshot.docs.map(doc => {
        const userData = doc.data();
        return {
          ...userData,
        id: doc.id,
          createdAt: userData.createdAt?.toDate(),
          updatedAt: userData.updatedAt?.toDate(),
          lastLoginAt: userData.lastLoginAt?.toDate()
        };
      }) as User[];
      setUsers(loadedUsers);
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.failedToLoad'));
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const createUser = async (user: User) => {
    try {
      const usersRef = collection(db, 'users');
      const docRef = await addDoc(usersRef, {
        ...user,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      const newUser = {
        ...user,
        id: docRef.id
      };
      // Update local state immediately
      setUsers(prevUsers => [...prevUsers, newUser]);
      toast.success(t('systemAdmin.messages.user.createdSuccess'));
      return newUser;
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.createdError'));
      throw error;
    }
  };

  const updateUser = async (id: string, data: Partial<User>) => {
    try {
      const userRef = doc(db, 'users', id);
      await updateDoc(userRef, data);

      // Update local state immediately
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === id
            ? { ...user, ...data }
            : user
        )
      );

      toast.success(t('systemAdmin.messages.user.updatedSuccess'));
      return { id, ...data };
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.updatedError'));
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      const userRef = doc(db, 'users', id);
      await deleteDoc(userRef);

      // Update local state immediately
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
      toast.success(t('systemAdmin.messages.user.deletedSuccess'));
      return true;
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.deletedError'));
      throw error;
    }
  };

  return { users, loading, error, createUser, updateUser, deleteUser, refresh: loadUsers };
};

